import { z } from "zod";
/**
 * Off-page steps 2 and 3: what kind of site it is, and what its pages say.
 *
 * Categories come from a site's own homepage (a declared structured-data type,
 * or a model's answer checked against a quote from the page). Page checks read
 * each cited page and match brand names in its main text — no model at all.
 */
/**
 * The nine site kinds, written out here rather than imported from `@crux/core`.
 *
 * This package is loaded by the web app in the browser, and `@crux/core` pulls
 * in Node-only modules (`node:crypto`). Importing the list from core blanked
 * every screen. `off-page-check.test.ts` fails if this copy and core's ever
 * differ, so the duplication cannot drift.
 */
export const SITE_CATEGORY_IDS = [
    "news_media",
    "niche_publication",
    "editorial_platform",
    "publishing_platform",
    "blog",
    "social_forum",
    "retailer_marketplace",
    "brand_manufacturer",
    "reference_other",
];
export const siteCategorySchema = z.enum(SITE_CATEGORY_IDS);
/**
 * A site's category as the screen should show it.
 *
 * `yours` is a workspace's own correction and always wins. `pending` means no
 * decision yet — shown, never hidden.
 */
export const siteKindSchema = z.discriminatedUnion("state", [
    z.object({
        state: z.literal("categorised"),
        category: siteCategorySchema,
        /**
         * `citations`: we could not read the site, so a model judged it from the
         * titles and addresses of pages this workspace's answers cited, and its
         * quote was found in them.
         */
        source: z.enum(["page_signal", "model", "citations", "yours"]),
        evidence: z.string().nullable(),
        evidenceUrl: z.string().nullable(),
    }),
    z.object({ state: z.literal("pending") }),
    z.object({
        state: z.literal("unreadable"),
        reason: z.enum(["unreadable", "refused", "undecided"]),
    }),
    z.object({ state: z.literal("hidden") }),
]);
export const siteCategoryUpdateSchema = z.object({
    domain: z.string().trim().min(1).max(253),
    /** Null clears the correction and goes back to the site's own category. */
    category: siteCategorySchema.nullable(),
    hidden: z.boolean(),
});
/**
 * Below this many answers, a share is not a rate.
 *
 * One answer that named you is "100%". True, and almost meaningless: the
 * denominator is one. It lives here rather than in the screen because the query
 * that orders the list and the column that draws it have to agree — sorting by
 * a share the screen then refuses to show as a share is how a list ends up
 * leading with its least informative rows.
 */
export const ENOUGH_TO_RATE = 3;
/** Whether a share of this many answers is worth reading as a percentage. */
export function ratable(whole) {
    return whole >= ENOUGH_TO_RATE;
}
/**
 * Categorise one site now, rather than when the scheduled pass reaches it.
 *
 * The pass runs every ten minutes and shares its budget between workspaces, so
 * a site nobody is looking at waits its turn. A site somebody has just opened
 * is not that site, and asking them to wait for a cron they cannot see is the
 * difference between "not yet" and "broken".
 */
export const siteCategoriseNowSchema = z.object({
    domain: z.string().trim().min(1).max(253),
});
export const siteCategorisedSchema = z.object({
    domain: z.string(),
    /** Null when the site could not be settled; `state` says what happened. */
    category: siteCategorySchema.nullable(),
    /**
     * `categorised` — settled. `undecided` — read, but it would not say what it
     * is. `unreadable` — we could not fetch it. `refused` — its robots.txt says
     * no, and we do not argue. `guessed` — the site itself could not be read, so
     * this comes from the titles of its pages that answers cited.
     */
    state: z.enum(["categorised", "guessed", "undecided", "unreadable", "refused"]),
    /** The words the decision rests on, so a reader can disagree with it. */
    evidence: z.string().nullable(),
});
export const PAGE_STATES = [
    "queued",
    "fetching",
    "read",
    "insufficient_text",
    "refused_by_robots",
    "blocked",
    "not_html",
    "too_large",
    "http_error",
    "unsafe_target",
    "failed",
];
export const pageStateSchema = z.enum(PAGE_STATES);
export const pagePresenceSchema = z.object({
    brandId: z.string(),
    brandName: z.string(),
    isSubject: z.boolean(),
    found: z.boolean(),
    listOnly: z.boolean(),
    quote: z.string().nullable(),
});
/** One page's latest reading. `presence` is empty unless `state` is `read`. */
export const pageReadingSchema = z.object({
    url: z.string(),
    state: pageStateSchema,
    failureCode: z.string().nullable(),
    fetchedAt: z.string().nullable(),
    presence: z.array(pagePresenceSchema),
});
/** A check page job. Parsed at the queue boundary like any other input (§4). */
export const offPageCheckJobSchema = z.object({
    workspaceId: z.string().min(1),
    checkId: z.string().min(1),
    url: z.string().min(1),
});
//# sourceMappingURL=off-page-check.js.map